/**
 * CaseEscalationSection — shared case-escalation + agent-suspension section, answer-only
 * (BR-AGENT-ESC-1, BR-AGENT-ESC-2, BR-ESC-AUTHZ, BR-ESC-RESOLVE).
 *
 * Extracted from `OverviewTab.tsx`'s local `CaseEscalationSection` so Repair Detail can
 * reuse it verbatim under a `repair-` test-id prefix, while the Lead's Overview tab keeps
 * its pre-existing `lead-` ids unchanged (`testIdPrefix` substitutes into every id below —
 * `${prefix}-open-escalation-card`, `${prefix}-agent-paused-banner`, etc).
 *
 * CRITICAL: render this SEPARATELY from the case/lead's agent-POLICY control block — a
 * paused agent is not a disabled one; folding the paused state into the policy control
 * block would misrepresent the landlord's own enablement setting.
 *
 * Dismiss is REMOVED (BR-ESC-RESOLVE) — answering is the only resolution path.
 *
 * The paused banner is driven by GET /cases/:id/agent-suspension (BR-AGENT-ESC-2), which
 * is the source of truth for whether the case is paused. Only 'landlord_takeover' pauses
 * the agent now — the agent runtime itself is what suspends/resumes on escalation via a
 * timed wake (run grant), so an OPEN escalation no longer holds the case paused
 * (BR-ESC-CONTINUE); 'escalation_open' is a historical cause value only, retained on the
 * enum for old data and never produced after the migration. Answering an escalation is
 * therefore completely independent of suspension state — it does not clear, set, or
 * otherwise touch any suspension cause. The explicit "Hand back to agent" control remains
 * the only action that ends a 'landlord_takeover' suspension.
 *
 * `takeoverText` lets each caller name the takeover cause in its own domain language
 * (Lead: a direct reply to the prospect; Repair: a direct message to a party) without
 * this shared component hardcoding either.
 */

import { useState, useCallback, useEffect } from 'react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';
import { WhyAgentStoppedNote } from './WhyAgentStoppedNote';
import { usePropertyTimezone } from '../../hooks/usePropertyTimezone';
import { formatTimestamp } from '../../utils/date';
import { asIsoDateTimeString } from '@resida/shared';
import {
  fetchOpenEscalationForCase,
  fetchAgentSuspension,
  answerEscalation,
  handBackAgent,
} from '../../api/agent-escalations.api';
import type {
  EscalationListItem as CaseEscalationItem,
  SuspensionStateResponse as SuspensionState,
} from '@resida/api-types/agent';

/** The cause holding the pause open; derived, not restated. */
type AgentSuspensionCause = SuspensionState['causes'][number];

/** Renders the cause holding the pause open. 'landlord_takeover' and 'manual_pause'
 *  (RA-PAUSE-CONTROL) both pause the agent now (BR-ESC-CONTINUE) — the generic fallback
 *  below covers a historical 'escalation_open'-only row, which cannot occur on a case
 *  created after the migration that retired escalation-driven suspension.
 *
 *  Exported for direct unit testing (RA-SUSPEND-BANNER): the "both causes open at once"
 *  branch cannot be driven through the UI in this iteration (CaseAgentPolicySection hides
 *  its "Pause agent" trigger once `agentState === 'paused'`, which a landlord_takeover
 *  suspension already produces before a manual pause could be layered on top via a click),
 *  so this pure string-formatting function is proven directly instead of through an e2e
 *  seam that the app's own affordances cannot reach. */
export function formatSuspensionCauseText(causes: AgentSuspensionCause[], takeoverText: string): string {
  const hasTakeover = causes.includes('landlord_takeover');
  const hasManualPause = causes.includes('manual_pause');
  if (hasTakeover && hasManualPause) {
    return `The agent is paused on this case because ${takeoverText}, and because you paused it manually.`;
  }
  if (hasManualPause) {
    return 'The agent is paused on this case because you paused it manually.';
  }
  if (hasTakeover) {
    return `The agent is paused on this case because ${takeoverText}.`;
  }
  return 'The agent is paused on this case.';
}

interface CaseEscalationSectionProps {
  caseId: string;
  /** Distinguishes Lead-detail vs Repair-detail test ids. */
  testIdPrefix: 'lead' | 'repair';
  /** Names the takeover cause in the caller's own domain language, substituted into
   *  formatSuspensionCauseText's takeover copy. */
  takeoverText: string;
  /** Refreshes the parent page's own state (events, case/repair, timeline) after a
   *  mutation here — its own activity view otherwise stays stale until the page reloads. */
  onChanged?: () => void;
  /** Bumped by the caller after a SIBLING mutation that can change this case's escalation
   *  or suspension state without this section's own knowledge — e.g. a party message sent
   *  via RepairComposer (raises 'landlord_takeover' server-side) or a repair/case close
   *  (clears the open escalation server-side). This section's own mutations (answer,
   *  hand-back) already reload themselves and don't need this. Included in the load
   *  effect's dependency array so a bump re-fetches in place, without remounting the
   *  section or losing its own in-progress UI state. */
  refreshToken?: number | string;
}

/** Exported for direct component testing (BR-AGENT-ESC-2 / BR-ESC-CONTINUE). */
export function CaseEscalationSection({
  caseId,
  testIdPrefix: p,
  takeoverText,
  onChanged,
  refreshToken,
}: CaseEscalationSectionProps) {
  const propertyTimezone = usePropertyTimezone();
  // undefined = loading, null = none open
  const [escalation, setEscalation] = useState<CaseEscalationItem | null | undefined>(undefined);
  // undefined = loading. Source of truth for paused/cause (BR-AGENT-ESC-2) — NOT inferred
  // from `escalation` above: the two are independent state now. Only 'landlord_takeover'
  // pauses the agent (BR-ESC-CONTINUE); an open escalation has no effect on this at all.
  const [suspension, setSuspension] = useState<SuspensionState | null | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [answering, setAnswering] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [handBackBusy, setHandBackBusy] = useState(false);
  const [handBackError, setHandBackError] = useState<string | null>(null);
  const [handedBackMsg, setHandedBackMsg] = useState<string | null>(null);
  // Transient confirmation after answering an escalation — the card unmounts as soon as
  // `escalation` goes null on reload, so this renders independently of the card.
  const [resolvedMsg, setResolvedMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [found, suspensionState] = await Promise.all([
        fetchOpenEscalationForCase(caseId),
        fetchAgentSuspension(caseId),
      ]);
      setEscalation(found);
      setSuspension(suspensionState);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load escalation status');
      setEscalation(null);
      setSuspension(null);
    }
  }, [caseId]);

  useEffect(() => {
    void load();
    // refreshToken deliberately included so a bump from a sibling mutation (party message
    // send, case/repair close) re-fetches in place — see the prop's doc comment above.
  }, [load, refreshToken]);

  const handleAnswer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!answerText.trim() || !escalation) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await answerEscalation(escalation.id, answerText.trim());
      if (result.status !== 'answered') {
        // Lost race: the case closed (BR-CASE-CLOSE-CLEANUP) between load and submit, so the
        // escalation was closed out as 'dismissed' and this answer was NOT recorded. Say so
        // rather than claiming it was sent.
        setActionError('This escalation was already resolved — the case was closed, so your answer was not sent.');
        await load();
        onChanged?.();
        return;
      }
      setAnswering(false);
      setAnswerText('');
      // Reload to drop the now-answered escalation from view — answering does not touch
      // suspension state at all (BR-ESC-CONTINUE), so this is purely about the escalation
      // list, not about whether the agent is paused.
      await load();
      onChanged?.();
      setResolvedMsg(
        "Answer sent — the agent will pick it up now, or when you hand the case back if you're handling it directly."
      );
      setTimeout(() => setResolvedMsg(null), 4000);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 409 || status === 404) {
        // RA-ESC-CARD lost race — this escalation was already resolved elsewhere (e.g.
        // the queue page) between load and submit.
        setActionError('This escalation was already resolved.');
        await load();
      } else {
        setActionError(err instanceof Error ? err.message : 'Failed to send answer');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleHandBack = async () => {
    setHandBackBusy(true);
    setHandBackError(null);
    try {
      // Unconditional — ends the suspension regardless of which cause(s) remain.
      await handBackAgent(caseId);
      await load();
      onChanged?.();
      setHandedBackMsg('Handed back — the agent will resume responding on this case.');
      setTimeout(() => setHandedBackMsg(null), 4000);
    } catch (err) {
      setHandBackError(err instanceof Error ? err.message : 'Failed to hand back to agent');
    } finally {
      setHandBackBusy(false);
    }
  };

  const paused = suspension?.suspended ?? false;
  const causes = suspension?.causes ?? [];
  const expiresAtLabel = suspension?.expiresAt
    ? formatTimestamp(asIsoDateTimeString(suspension.expiresAt), propertyTimezone)
    : null;

  if ((escalation === undefined || suspension === undefined) && !loadError) {
    return (
      <section data-testid={`${p}-escalation-section-loading`} className="mb-6" aria-busy="true">
        <Skeleton className="h-20 w-full rounded-lg" />
      </section>
    );
  }

  if (loadError) {
    return (
      <section
        data-testid={`${p}-escalation-section-error`}
        className="mb-6 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
        role="alert"
      >
        <p className="mb-2">{loadError}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void load()}
          data-testid={`${p}-escalation-section-retry`}
        >
          Retry
        </Button>
      </section>
    );
  }

  return (
    <>
      {/* Success confirmation after answering an escalation — rendered independently of
          the card below, since `escalation` goes null (and the card unmounts) as soon as
          `load()` re-fetches. */}
      {resolvedMsg && (
        <div
          role="status"
          aria-live="polite"
          data-testid={`${p}-open-escalation-resolved-success`}
          className="mb-4 p-3 rounded-md border border-success/20 bg-success/10 text-success text-sm"
        >
          {resolvedMsg}
        </div>
      )}

      {/* Hand-back confirmation. Rendered OUTSIDE the paused banner for the same reason
          the resolve message above sits outside the card: a successful hand-back sets
          `paused` false, which unmounts that banner — so a message nested inside it could
          never actually be seen. */}
      {handedBackMsg && (
        <div
          role="status"
          aria-live="polite"
          data-testid={`${p}-agent-hand-back-success`}
          className="mb-4 p-3 rounded-md border border-success/20 bg-success/10 text-success text-sm"
        >
          {handedBackMsg}
        </div>
      )}

      {/* Paused-agent banner — a transient control-plane state, NOT the case/lead's own
          agent enablement/review setting (BR-AGENT-ESC-2). Kept structurally and visually
          distinct from the agent-policy section the caller renders alongside it. */}
      {paused && (
        <section
          data-testid={`${p}-agent-paused-banner`}
          // Dark-800-on-light-100 pairing (not text-warning on bg-warning/10, which measured
          // ~2.1:1 and failed WCAG AA) — matches RepairAgentStateBadge's 'paused' entry, this
          // feature's most safety-critical status indicator (iteration-12 web-ui review, FM2).
          className="mb-6 rounded-lg border border-warning/25 bg-warning/15 p-4"
          role="status"
          aria-labelledby={`${p}-agent-paused-title`}
        >
          <h2 id={`${p}-agent-paused-title`} className="text-sm font-semibold text-warning mb-1">
            Agent paused
          </h2>
          <p
            className={expiresAtLabel ? 'text-sm text-warning mb-1' : 'text-sm text-warning mb-3'}
            data-testid={`${p}-agent-paused-cause`}
          >
            {formatSuspensionCauseText(causes, takeoverText)}
          </p>
          {expiresAtLabel && (
            <p className="text-xs text-warning mb-3" data-testid={`${p}-agent-paused-expires`}>
              If not cleared sooner, this resumes automatically on {expiresAtLabel}.
            </p>
          )}
          {handBackError && (
            <div role="alert" className="mb-2 text-xs text-destructive">{handBackError}</div>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleHandBack()}
            disabled={handBackBusy}
            data-testid={`${p}-agent-hand-back-button`}
            aria-label={
              causes.length > 1
                ? 'Hand this case back to the agent, clearing every reason it is paused'
                : 'Hand this case back to the agent'
            }
          >
            {handBackBusy ? 'Handing back…' : 'Hand back to agent'}
          </Button>
        </section>
      )}

      {/* Open escalation card (BR-AGENT-ESC-1) */}
      {escalation && (
        <section
          data-testid={`${p}-open-escalation-card`}
          className="mb-6 rounded-lg border border-border bg-card p-5"
          aria-labelledby={`${p}-open-escalation-title`}
        >
          <h2 id={`${p}-open-escalation-title`} className="text-base font-semibold mb-1">
            Escalated to you
          </h2>
          <p className="text-sm text-muted-foreground mb-3">
            The agent needs your input before it can continue.
          </p>
          {escalation.qualityReviewBlocks.map((block, n) => (
            <WhyAgentStoppedNote
              key={`${block.at}-${n}`}
              data-testid={`${p}-open-escalation-why-stopped-${n}`}
              critique={block.critique}
              tier={block.tier}
              itemText={block.itemText}
              blockedArgsSummary={block.blockedArgsSummary}
            />
          ))}
          <div
            data-testid={`${p}-open-escalation-question`}
            className="mb-4 p-3 rounded-md bg-muted/60 text-sm text-foreground whitespace-pre-wrap border border-border"
          >
            {escalation.question}
          </div>

          {actionError && (
            <div
              role="alert"
              className="mb-3 p-2 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-xs"
            >
              {actionError}
            </div>
          )}

          {answering ? (
            <form
              onSubmit={(e) => void handleAnswer(e)}
              className="space-y-2"
              data-testid={`${p}-open-escalation-answer-form`}
            >
              <label htmlFor={`${p}-escalation-answer`} className="block text-xs font-medium text-muted-foreground">
                Reply to the agent
              </label>
              <Textarea
                id={`${p}-escalation-answer`}
                data-testid={`${p}-open-escalation-answer-input`}
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                rows={3}
                maxLength={4000}
                placeholder="Write your answer here…"
                className="resize-y"
                aria-label="Answer to the agent's escalation question"
                aria-describedby={`${p}-escalation-answer-help`}
                required
              />
              <p id={`${p}-escalation-answer-help`} className="text-xs text-muted-foreground">
                A short reply is fine — e.g. “No approval needed, go ahead” or “I’ve handled it
                directly: …”.
              </p>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={busy || !answerText.trim()}
                  data-testid={`${p}-open-escalation-answer-submit`}
                  aria-label="Send answer for this escalation"
                >
                  {busy ? 'Sending…' : 'Send answer'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setAnswering(false); setActionError(null); }}
                  disabled={busy}
                  data-testid={`${p}-open-escalation-answer-cancel`}
                  aria-label="Cancel answering"
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                onClick={() => setAnswering(true)}
                disabled={busy}
                data-testid={`${p}-open-escalation-answer-button`}
                aria-label="Answer this escalation"
              >
                Answer
              </Button>
            </div>
          )}
        </section>
      )}
    </>
  );
}

export default CaseEscalationSection;
